import { useEffect, useRef, useState } from "react";
import { X, Play, Pause, Download, Send, Film } from "lucide-react";
import { proxyMediaUrl } from "@/lib/media";

/**
 * VideoReadyPopup — floating bottom-right notification that fires the moment
 * a video generation job flips to `done`. Solves the "did my video render?"
 * anxiety and doubles as a manual escape hatch when the feed insertion path
 * misses (e.g. the tab was in the background when polling completed).
 *
 * Buttons:
 *   • PLAY / PAUSE — inline preview inside the popup itself
 *   • DOWNLOAD     — saves the mp4 with a friendly filename
 *   • SEND TO FEED — explicitly re-injects the completed item into the
 *                    Studio feed so the user can access it there. Idempotent —
 *                    if the item is already in the feed it just scrolls to it.
 *
 * Styling follows Geneo's dark Afro Sci-Fi palette: obsidian glass,
 * gold border, cyan accents on primary CTAs.
 */
export function VideoReadyPopup({ item, onDismiss, onSendToFeed, onOpenDetail }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    // Reset play/sent state whenever a new item arrives.
    setPlaying(false);
    setSent(false);
  }, [item?.id]);

  if (!item) return null;

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  const download = async () => {
    try {
      const res = await fetch(proxyMediaUrl(item.media_url));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `geneo-video-${(item.id || "clip").slice(0, 6)}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      // Fall back to opening in a new tab so the user can save manually.
      window.open(item.media_url, "_blank", "noopener");
    }
  };

  const sendToFeed = () => {
    onSendToFeed?.(item);
    setSent(true);
    // Small delay so the checkmark state is visible before the popup dismisses.
    setTimeout(() => onDismiss?.(), 800);
  };

  return (
    <div
      className="fixed bottom-6 right-6 z-[100] w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl overflow-hidden border border-[#F2C94C]/45 bg-[#0A0A0A]/92 backdrop-blur-2xl shadow-[0_20px_70px_-12px_rgba(242,201,76,0.45)] animate-in slide-in-from-bottom-4 fade-in duration-300"
      data-testid="video-ready-popup"
    >
      {/* Aurora rim */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            "radial-gradient(120% 60% at 0% 0%, rgba(242,201,76,0.10), transparent 55%), radial-gradient(120% 60% at 100% 100%, rgba(0,229,255,0.10), transparent 55%)",
        }}
      />

      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-2">
            <Film className="w-3.5 h-3.5 text-[#F2C94C]" />
            <span className="font-mono-hy text-[10px] tracking-[0.28em] text-[#F2C94C]">VIDEO READY</span>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            data-testid="video-ready-close"
            className="text-white/50 hover:text-white transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Prompt preview */}
        {item.prompt && (
          <div
            className="px-4 pb-2 font-body text-[12px] text-white/80 line-clamp-2"
            title={item.prompt}
          >
            {item.prompt}
          </div>
        )}

        {/* Video thumbnail */}
        <div className="relative mx-4 rounded-xl overflow-hidden border border-white/[0.08] bg-black">
          <video
            ref={videoRef}
            src={proxyMediaUrl(item.media_url)}
            className="w-full aspect-video object-cover"
            muted
            playsInline
            loop
            preload="metadata"
            onEnded={() => setPlaying(false)}
            data-testid="video-ready-preview"
          />
          <button
            type="button"
            onClick={togglePlay}
            aria-label={playing ? "Pause" : "Play"}
            data-testid="video-ready-play"
            className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/35 transition-colors group"
          >
            <span className="w-12 h-12 rounded-full bg-[#F2C94C]/95 text-[#0A0A0A] flex items-center justify-center shadow-[0_0_28px_-4px_rgba(242,201,76,0.75)] group-hover:scale-105 transition-transform">
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </span>
          </button>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-3 gap-2 p-4">
          <button
            type="button"
            onClick={() => onOpenDetail?.(item)}
            data-testid="video-ready-open"
            className="inline-flex flex-col items-center gap-1 py-2 rounded-lg border border-[#F2C94C]/30 hover:border-[#F2C94C] hover:bg-[#F2C94C]/10 text-[#F2C94C] transition-all"
            title="Open in Studio"
          >
            <Play className="w-3.5 h-3.5" />
            <span className="font-mono-hy text-[9px] tracking-[0.2em]">OPEN</span>
          </button>
          <button
            type="button"
            onClick={download}
            data-testid="video-ready-download"
            className="inline-flex flex-col items-center gap-1 py-2 rounded-lg border border-[#F2C94C]/30 hover:border-[#F2C94C] hover:bg-[#F2C94C]/10 text-[#F2C94C] transition-all"
            title="Download mp4"
          >
            <Download className="w-3.5 h-3.5" />
            <span className="font-mono-hy text-[9px] tracking-[0.2em]">DOWNLOAD</span>
          </button>
          <button
            type="button"
            onClick={sendToFeed}
            disabled={sent}
            data-testid="video-ready-send-feed"
            className={`inline-flex flex-col items-center gap-1 py-2 rounded-lg border transition-all ${
              sent
                ? "border-[#00E5FF] bg-[#00E5FF]/25 text-[#00E5FF]"
                : "border-[#00E5FF]/45 bg-[#00E5FF]/10 hover:bg-[#00E5FF]/20 hover:border-[#00E5FF] text-[#00E5FF]"
            }`}
            title="Push this card into the Studio feed"
          >
            <Send className="w-3.5 h-3.5" />
            <span className="font-mono-hy text-[9px] tracking-[0.2em]">{sent ? "SENT" : "SEND TO FEED"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default VideoReadyPopup;
